(() => {
  "use strict";

  const CUSTOM_EVENT_NAME = "ballonnenshop_helium_configurator_v1";
  const FLOW_VERSION = "helium_v1";
  const MAX_RUN_AGE_MS = 4 * 60 * 60 * 1000;
  const EVENT_NAMES = new Set([
    "configurator_product_view",
    "configurator_cta_click",
    "configurator_start",
    "configurator_step_view",
    "configurator_option_change",
    "configurator_step_complete",
    "configurator_back",
    "configurator_cart_submit",
    "configurator_cart_success",
    "configurator_cart_error",
    "configurator_error"
  ]);
  const OPTIONAL_FIELDS = new Set([
    "step_id",
    "step_position",
    "step_visit_id",
    "from_step_id",
    "to_step_id",
    "completion_type",
    "option_group",
    "option_variant_id",
    "previous_option_variant_id",
    "selection_action",
    "interaction_source",
    "error_stage",
    "error_type",
    "http_status",
    "elapsed_ms",
    "delivery_lead_days",
    "delivery_selection_source",
    "item_count",
    "card_selected",
    "weight_selected",
    "gift_selected",
    "has_message",
    "device_class"
  ]);
  let memoryRun = null;
  let productViewKey = null;

  const normalizeShopifyId = (value) => {
    const normalized = String(value || "");
    return /^\d{1,30}$/.test(normalized) ? normalized : "";
  };

  const getDeviceClass = () => {
    const width = Number(window.innerWidth);
    if (!Number.isFinite(width) || width <= 0) return "desktop";
    if (width < 768) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
  };

  const isUuid = (value) => typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

  const createUuid = () => {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return window.crypto.randomUUID();
      }
      if (window.crypto && typeof window.crypto.getRandomValues === "function") {
        const bytes = new Uint8Array(16);
        window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 15) | 64;
        bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
      }
    } catch (_) {}
    return null;
  };

  const persistRun = (run) => {
    memoryRun = run;
  };

  const readRun = () => {
    const run = memoryRun;
    if (!run || !isUuid(run.config_instance_id) ||
        !Number.isInteger(run.event_sequence) ||
        !Number.isInteger(run.started_at_ms) ||
        !Number.isInteger(run.last_occurred_at_ms) ||
        Date.now() - run.started_at_ms > MAX_RUN_AGE_MS) {
      return null;
    }
    return run;
  };

  const startRun = ({ productId, variantId }) => {
    const configInstanceId = createUuid();
    const product_id = normalizeShopifyId(productId);
    const base_variant_id = normalizeShopifyId(variantId);
    if (!configInstanceId || !product_id || !base_variant_id) return null;
    const startedAtMs = Date.now();
    const run = {
      config_instance_id: configInstanceId,
      event_sequence: 0,
      started_at_ms: startedAtMs,
      last_occurred_at_ms: startedAtMs - 1,
      product_id,
      base_variant_id
    };
    persistRun(run);
    return run;
  };

  const ensureRun = ({ productId, variantId }) => {
    const product_id = normalizeShopifyId(productId);
    const base_variant_id = normalizeShopifyId(variantId);
    const current = readRun();
    if (current && current.product_id === product_id && current.base_variant_id === base_variant_id) {
      return current;
    }
    return startRun({ productId, variantId });
  };

  const emit = (eventName, fields = {}) => {
    if (!EVENT_NAMES.has(eventName)) return null;
    const run = readRun();
    const eventId = createUuid();
    if (!run || !eventId) return null;

    run.event_sequence += 1;
    run.last_occurred_at_ms = Math.max(Date.now(), run.last_occurred_at_ms + 1);
    persistRun(run);
    const payload = {
      schema_version: 1,
      event_id: eventId,
      config_instance_id: run.config_instance_id,
      event_sequence: run.event_sequence,
      occurred_at: new Date(run.last_occurred_at_ms).toISOString(),
      event_name: eventName,
      flow_version: FLOW_VERSION,
      product_id: run.product_id,
      base_variant_id: run.base_variant_id,
      device_class: getDeviceClass()
    };

    for (const [key, value] of Object.entries(fields)) {
      if (!OPTIONAL_FIELDS.has(key) || value === undefined || value === null) continue;
      if (!["string", "number", "boolean"].includes(typeof value)) continue;
      payload[key] = value;
    }

    try {
      const publishResult = window.Shopify?.analytics?.publish?.(CUSTOM_EVENT_NAME, payload);
      if (publishResult && typeof publishResult.catch === "function") publishResult.catch(() => {});
    } catch (_) {}
    return payload;
  };

  const emitProductView = ({ productId, variantId }) => {
    const product_id = normalizeShopifyId(productId);
    const base_variant_id = normalizeShopifyId(variantId);
    if (!product_id || !base_variant_id) return null;
    const nextProductViewKey = `${product_id}:${base_variant_id}`;
    if (productViewKey === nextProductViewKey) return null;
    const run = ensureRun({ productId, variantId });
    if (!run) return null;
    productViewKey = nextProductViewKey;
    return emit("configurator_product_view");
  };

  const clearRun = () => {
    memoryRun = null;
    productViewKey = null;
  };

  window.BallonnenshopConfiguratorAnalytics = {
    CUSTOM_EVENT_NAME,
    clearRun,
    createUuid,
    emit,
    emitProductView,
    ensureRun,
    readRun,
    startRun
  };
})();
